import React from 'react';
import { AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * ConfirmActionCard — rendered inline inside the chat when the agent
 * stages a high-impact action (mass email, publish, etc.). The parent
 * calls /api/ai-agent/confirm when the user clicks Confirm.
 */
export const ConfirmActionCard = ({
  pending,
  onConfirm,
  onCancel,
  busy,
  done,
  success,
}) => {
  if (!pending) return null;
  const { tool_id, preview } = pending;

  const Row = ({ label, value }) => (
    <div className="flex items-start justify-between gap-3 py-1.5 text-[12.5px]">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="font-semibold text-right break-words text-[#1a1a1a]">{value}</span>
    </div>
  );

  return (
    <div
      className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4 mt-2"
      data-testid={`ai-confirm-card-${tool_id}`}
    >
      <div className="flex items-start gap-2 mb-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-bold text-amber-900">Confirmation required</div>
          <div className="text-xs text-amber-800">
            This is a high-impact action. Review and tap Confirm to proceed.
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-amber-200 p-3">
        <Row label="Action" value={preview?.action || tool_id} />
        {preview?.subject && <Row label="Subject" value={preview.subject} />}
        {preview?.title && <Row label="Title" value={preview.title} />}
        {preview?.audience_label && (
          <Row label="Audience" value={preview.audience_label} />
        )}
        {typeof preview?.eligible_recipients === 'number' && (
          <Row
            label="Eligible recipients"
            value={preview.eligible_recipients.toLocaleString()}
          />
        )}
        {preview?.date && <Row label="Date" value={preview.date} />}
        {preview?.set_featured !== undefined && (
          <Row label="Featured" value={preview.set_featured ? 'Yes' : 'No'} />
        )}
        {preview?.has_image !== undefined && (
          <Row label="Has image" value={preview.has_image ? 'Yes' : 'No — consider generating one first'} />
        )}
        {preview?.test_mode && (
          <Row label="Test mode" value={`→ ${preview.test_email || 'actor'}`} />
        )}
        {preview?.body_html_preview && (
          <div className="mt-3 pt-3 border-t border-amber-100">
            <div className="text-[11px] uppercase font-semibold text-slate-500 tracking-wide mb-1">
              Body preview
            </div>
            <div
              className="text-[12px] text-slate-700 max-h-40 overflow-y-auto"
              dangerouslySetInnerHTML={{ __html: preview.body_html_preview }}
            />
          </div>
        )}
      </div>

      {done ? (
        <div className="mt-3 flex items-center gap-2 text-[13px] font-semibold text-emerald-700">
          <CheckCircle2 className="w-4 h-4" />
          {success ? 'Action executed successfully.' : 'Action failed — see tool result above.'}
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2">
          <Button
            onClick={onConfirm}
            disabled={busy}
            className="bg-amber-600 hover:bg-amber-700 flex-1"
            data-testid={`ai-confirm-${tool_id}`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {busy ? 'Executing…' : 'Confirm & execute'}
          </Button>
          <Button
            onClick={onCancel}
            disabled={busy}
            variant="outline"
            className="text-slate-600 border-slate-300"
            data-testid={`ai-cancel-${tool_id}`}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

export default ConfirmActionCard;
